import { useTranslations } from '@vocab/react';
import { Box, Button } from 'braid-design-system';
import { observer } from 'mobx-react';

import { HOTJAR_TAG_EMAIL } from 'src/constants/hotjar';
import type { PageConfig } from 'src/libs/PageConfig';
import { sendHotjarTag } from 'src/utils/hotjar';

import type { IProspectEmailStore } from '../../../stores/JobApplicationStore/ProspectEmailStore';
import { ACTION_ERROR } from '../../../stores/state';
import { trackClickedSendEmail } from '../../../utils/events';

import { parseEmailSubjectLength } from './../../../utils/parseEmailSubjectLength';
import translations from './.vocab';
import SendProspectEmailForm from './SendProspectEmailForm/SendProspectEmailForm';

interface Props {
  prospectEmailStore: IProspectEmailStore;
  showForm: boolean;
  setShowForm: (value: boolean) => void;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const SendProspectEmail = ({
  prospectEmailStore,
  showForm,
  setShowForm,
  addCustomError,
}: Props) => {
  const { t } = useTranslations(translations);

  const onSubmitSuccess = () => {
    setShowForm(false);
  };

  const onSendProspectEmail = (
    subject: string,
    message: string,
    enableReplies: boolean,
  ) =>
    prospectEmailStore
      .sendEmail({
        subject,
        message,
        enableReplies,
        addCustomError,
      })
      .then((state: string) => state !== ACTION_ERROR);

  const onCancel = () => {
    setShowForm(false);
  };

  if (showForm) {
    const emailSubject = t(
      'advertiserName has responded to your application for jobTitle',
      {
        advertiserName:
          prospectEmailStore.rootStore.userStore.user.advertiserName,
        jobTitle: prospectEmailStore.rootStore.job.jobInformation.title,
      },
    );

    const messageDefaultText = t('Hi firstName,', {
      firstName: prospectEmailStore.jobApplication.firstName,
    });

    return (
      <SendProspectEmailForm
        onSubmitSuccess={onSubmitSuccess}
        onSendProspectEmail={onSendProspectEmail}
        onCancel={onCancel}
        fromName={prospectEmailStore.rootStore.userStore.user.name}
        fromEmail={prospectEmailStore.rootStore.userStore.user.email}
        messageDefaultText={messageDefaultText}
        subjectDefaultText={parseEmailSubjectLength(emailSubject)}
        selectedJobApplicationsCount={0}
        addCustomError={addCustomError}
      />
    );
  }

  const onCreateEmailClick = () => {
    trackClickedSendEmail(prospectEmailStore.trackingAttributes);
    sendHotjarTag(HOTJAR_TAG_EMAIL);
    setShowForm(true);
  };

  return (
    <Box display="inlineBlock">
      <Button onClick={onCreateEmailClick} variant="transparent" bleed>
        {t('Send email')}
      </Button>
    </Box>
  );
};

export default observer(SendProspectEmail);
